import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  cleanObject,
  rearrangeChowdeckProductList,
} from "../../../../../helpers";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useConnectChowdeckProducts,
  useUpdateConnectChowdeckProducts,
  useUpdateStoreMappingSettings,
} from "../../../../../hooks/api/mutations/useChowdeck";
import { ToastContext } from "../../../../../hooks/context";
import { pathConstants } from "../../../../../routes/pathContants";

export const useChowdeckProductLink = ({
  isEditMode = false,
  productList = [],
  lumiProductList = [],
  providerKey = "",
  storeMappingDetails = {},
}) => {
  const { connection_id } = useParams();
  const navigate = useNavigate();
  const triggerToast = useContext(ToastContext);
  const [search] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSucessView, setIsSucessView] = useState(false);
  const [showUnmappedWarning, setShowUnmappedWarning] = useState(false);
  const reArrangedProps = rearrangeChowdeckProductList(productList);
  const [selectedProducts, setSelectedProducts] = useState({
    selected_ids: reArrangedProps?.selectedIds || {},
    exclude_selected_ids: {},
    is_select_all: false,
  });

  const [storeMappingSettings, setStoreMappingSettings] = useState({
    sync_product_name: storeMappingDetails?.sync_product_name || false,
    sync_product_price: storeMappingDetails?.sync_product_price || false,
    price_markup_percentage: storeMappingDetails?.price_markup_percentage || "",
    price_recording_option:
      storeMappingDetails?.price_recording_option || "PROVIDER_TOTAL",
  });

  // IMPORTANT: ref prevents rerender of 5000 rows
  const selectionDataRef = useRef(reArrangedProps?.selectedData || {});

  useEffect(() => {
    if (!productList?.length) return;
    const reArranged = rearrangeChowdeckProductList(productList);
    selectionDataRef.current = {
      ...(reArranged?.selectedData || {}),
      ...selectionDataRef.current,
    };
    setSelectedProducts((prev) => ({
      ...prev,
      selected_ids: {
        ...(reArranged?.selectedIds || {}),
        ...prev.selected_ids,
      },
    }));
  }, [productList]);

  const toggleCheckbox = useCallback((id) => {
    if (!id) return;

    setSelectedProducts((prev) => {
      const selectedIds = { ...prev.selected_ids };
      const excludedIds = { ...prev.exclude_selected_ids };

      if (prev.is_select_all) {
        if (excludedIds[id]) delete excludedIds[id];
        else {
          excludedIds[id] = true;
          delete selectionDataRef.current[id];
        }
      } else {
        if (selectedIds[id]) {
          delete selectedIds[id];
          delete selectionDataRef.current[id];
        } else {
          selectedIds[id] = true;
        }
      }

      return {
        ...prev,
        selected_ids: selectedIds,
        exclude_selected_ids: excludedIds,
      };
    });
  }, []);

  const setSelectionData = useCallback((id, value) => {
    selectionDataRef.current[id] = value;
  }, []);

  const filteredProductList = useMemo(() => {
    const q = searchQuery?.trim().toLowerCase() || "";

    return productList?.filter(
      (item) =>
        (!q || item?.marketplace_product_name?.toLowerCase().includes(q)) &&
        item?.marketplace_product_ref &&
        item?.marketplace_product_ref !== "",
    );
  }, [productList, searchQuery]);

  const itemData = useMemo(
    () => ({
      productList: filteredProductList,
      lumiProductList,
      selectedProducts,
      selectionDataRef,
      storeMappingSettings,
      providerKey,
      setSelectionData,
      toggleCheckbox,
    }),
    [
      filteredProductList,
      storeMappingSettings,
      lumiProductList,
      selectedProducts,
      toggleCheckbox,
    ],
  );

  const getDataForSubmission = () => {
    const selectionData = selectionDataRef.current;

    const product_mapping = Object.entries(selectionData)
      .filter(([_, value]) => {
        return (
          value &&
          (value.product_id ||
            value.variant_id ||
            value.service_id ||
            value.lumi_object_id)
        );
      })
      .map(([marketplaceRef, value]) => {
        const {
          marketplace_product_name = "",
          product_id = "",
          variant_id = "",
          service_id = "",
          markup_override = undefined,
        } = value;

        return cleanObject({
          marketplace_product_ref: marketplaceRef,
          marketplace_product_name,
          markup_override,
          lumi_product: cleanObject({
            product_id,
            variant_id,
            service_id,
          }),
        });
      });

    return {
      product_mapping,
      store_mapping_id: search.get("mapping_id"),
    };
  };

  const { mutate: connectProducts, isLoading: isConnecting } =
    useConnectChowdeckProducts({ queryParams: { connection_id } });

  const { mutate: updateProducts, isLoading: isUpdating } =
    useUpdateConnectChowdeckProducts({ queryParams: { connection_id } });

  const { mutate: updateStoreSettings, isLoading: isUpdatingSettings } =
    useUpdateStoreMappingSettings({
      id: search.get("mapping_id"),
      queryParams: { connection_id },
    });

  const handleMapping = (force = false) => {
    if (!force && hasUnmappedProducts()) {
      setShowUnmappedWarning(true);
      return;
    }

    setShowUnmappedWarning(false);
    const func = isEditMode ? updateProducts : connectProducts;

    func(getDataForSubmission(), {
      onSuccess: () => {
        triggerToast("Product mapped successfully", "success");
        if (providerKey !== "squareup") return setIsSucessView(true);

        navigate(pathConstants.APPS_SQUAREUP_CONNECTED_STORE);
      },
    });
  };

  const hasUnmappedProducts = () => {
    const selectionData = selectionDataRef.current;

    return productList?.some((p) => {
      const ref = p?.marketplace_product_ref;
      if (!ref) return false;
      const data = selectionData[ref];
      return !(
        data &&
        (data.product_id ||
          data.variant_id ||
          data.service_id ||
          data.lumi_object_id)
      );
    });
  };

  const handleStoreSettings = () => {
    updateStoreSettings(cleanObject({ ...storeMappingSettings }), {
      onSuccess: () => {
        triggerToast("Store setting updated successfully", "success");
      },
    });
  };

  return {
    itemData,
    searchQuery,
    filteredProductList,
    selectedProducts,
    isSucessView,
    showUnmappedWarning,
    isLoading: isConnecting || isUpdating,
    storeMappingSettings,
    isUpdatingSettings,
    setStoreMappingSettings,
    setIsSucessView,
    setSelectedProducts,
    setSearchQuery,
    setShowUnmappedWarning,
    handleMapping,
    handleStoreSettings,
  };
};
